import React, { useEffect, useState } from 'react';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP: Record<AvatarSize, { px: number; fs: number }> = {
  xs: { px: 24, fs: 9 },
  sm: { px: 32, fs: 11 },
  md: { px: 36, fs: 13 },
  lg: { px: 44, fs: 15 },
  xl: { px: 56, fs: 18 },
};

interface AvatarProps {
  firstName: string;
  lastName?: string;
  color?: string;
  src?: string;
  size?: AvatarSize;
  style?: object;
}

export default function Avatar({ firstName, lastName, color = '#4F46E5', src, size = 'md', style }: AvatarProps) {
  const { px, fs } = SIZE_MAP[size];
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

  // SK Drive avatar URLs are signed and expire; a stale one shouldn't render
  // as a broken image — fall back to initials instead. Reset when `src`
  // changes so a fresh signed URL (e.g. after a new upload) gets a fresh try.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={`${firstName} ${lastName}`}
        onError={() => setFailed(true)}
        style={{
          width: px, height: px, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0, ...style,
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: px, height: px, borderRadius: '50%',
        background: color,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: fs, fontWeight: 600, color: 'white',
        fontFamily: 'var(--font-sans)', flexShrink: 0,
        userSelect: 'none',
        ...style,
      }}
    >
      {initials}
    </div>
  );
}
